import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import AuthContext from './AuthContext';
import MerchantContext from './MerchantContext';
import { useTRPC, RentalsPaymentsPortalRouterOutputs } from '../trpc/client/index';
import { getPalommaPlusIvaBreakdown } from '@/data/subscriptions';
import { useInvoices } from '../hooks/useInvoices';
import { useMutation, useQuery } from '@tanstack/react-query';

export type SubscriptionChoice = boolean | null; // null = not selected, true = yes, false = no

type SubscriptionState = RentalsPaymentsPortalRouterOutputs['subscriptions']['getState'];
export type PalommaPlusOffer = SubscriptionState['offer'];
export type CustomerSubscription = SubscriptionState['customerSubscription'];
export type SubscriptionExperiments = RentalsPaymentsPortalRouterOutputs['subscriptions']['getExperiments'];
export type PuntosColombiaAccruals = RentalsPaymentsPortalRouterOutputs['subscriptions']['listPuntosColombiaAccruals'];

type RetryPuntosColombiaAccrualInput = {
  subscriptionChargeId: string;
  puntosColombiaDocumentType: 'cc' | 'ce' | 'nit';
  puntosColombiaDocumentNumber: string;
};

type SubscriptionsContextType = {
  isEnabled: boolean;
  hasActiveSubscription: boolean;
  showSubscriptionOffer: boolean;
  selectedSubscription: SubscriptionChoice;
  selectSubscription: (value: boolean) => void;
  totalSubscriptionAmount: number;
  subscriptionIvaBreakdown: { base: number; iva: number } | null;
  subscriptionToCharge: { price: number } | null;
  customerSubscription: CustomerSubscription;
  offer: PalommaPlusOffer;
  cancelSubscription: (input: { reason: string; isInPencilExperiment?: boolean }) => Promise<void>;
  experiments?: SubscriptionExperiments;
  isLoading: boolean;
  refetchActive: () => void;
  puntosColombiaAccruals: PuntosColombiaAccruals;
  puntosColombiaAccrualsLoading: boolean;
  retryPuntosColombiaAccrual: (input: RetryPuntosColombiaAccrualInput) => Promise<void>;
  isRetryingPuntosColombiaAccrual: boolean;
  refetchPuntosColombiaAccruals: () => void;
};

const SubscriptionsContext = createContext<SubscriptionsContextType>({
  isEnabled: false,
  hasActiveSubscription: false,
  showSubscriptionOffer: false,
  selectedSubscription: null,
  selectSubscription: () => {},
  totalSubscriptionAmount: 0,
  subscriptionIvaBreakdown: null,
  subscriptionToCharge: null,
  customerSubscription: null,
  offer: null,
  cancelSubscription: async () => {},
  experiments: undefined,
  isLoading: false,
  refetchActive: () => {},
  puntosColombiaAccruals: [],
  puntosColombiaAccrualsLoading: false,
  retryPuntosColombiaAccrual: async () => {},
  isRetryingPuntosColombiaAccrual: false,
  refetchPuntosColombiaAccruals: () => {},
});

type SubscriptionsProviderProps = React.PropsWithChildren<{
  customerDocumentNumberOverride?: string;
  hasPendingInvoicesOverride?: boolean;
  rentalsMerchantIdOverride?: string;
}>;

const SubscriptionsProvider = ({
  children,
  customerDocumentNumberOverride,
  hasPendingInvoicesOverride,
  rentalsMerchantIdOverride,
}: SubscriptionsProviderProps): JSX.Element => {
  const trpc = useTRPC();
  const { merchant } = useContext(MerchantContext);
  const { documentNumber } = useContext(AuthContext);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionChoice>(null);

  const isEnabled = merchant?.subscriptionConfig?.enabled ?? false;

  const effectiveMerchantId = rentalsMerchantIdOverride ?? merchant?.id ?? '';
  const effectiveCustomerDocumentNumber = customerDocumentNumberOverride ?? documentNumber ?? '';

  const stateQueryOptions = trpc.subscriptions.getState.queryOptions({
    rentalsMerchantId: effectiveMerchantId,
    customerDocumentNumber: effectiveCustomerDocumentNumber,
  });

  const stateQuery = useQuery({
    ...stateQueryOptions,
    enabled: !!effectiveMerchantId && !!effectiveCustomerDocumentNumber,
  });

  const experimentsQueryOptions = trpc.subscriptions.getExperiments.queryOptions({
    rentalsMerchantId: effectiveMerchantId,
    customerDocumentNumber: effectiveCustomerDocumentNumber,
  });

  const experimentsQuery = useQuery({
    ...experimentsQueryOptions,
    enabled: !!effectiveMerchantId && !!effectiveCustomerDocumentNumber,
  });

  const cancelMutation = useMutation({
    ...trpc.subscriptions.cancel.mutationOptions(),
  });

  const accrualsQueryOptions = trpc.subscriptions.listPuntosColombiaAccruals.queryOptions({
    rentalsMerchantId: effectiveMerchantId,
    customerDocumentNumber: effectiveCustomerDocumentNumber,
  });

  const accrualsQuery = useQuery({
    ...accrualsQueryOptions,
    enabled: !!effectiveMerchantId && !!effectiveCustomerDocumentNumber,
  });

  const retryAccrualMutation = useMutation({
    ...trpc.subscriptions.retryPuntosColombiaAccrual.mutationOptions(),
  });

  const { refetch: refetchAccrualsQuery } = accrualsQuery;

  const refetchPuntosColombiaAccruals = useCallback(() => {
    void refetchAccrualsQuery();
  }, [refetchAccrualsQuery]);

  const retryPuntosColombiaAccrual = useCallback(
    async (input: RetryPuntosColombiaAccrualInput) => {
      if (!merchant?.id || !effectiveCustomerDocumentNumber) return;
      await retryAccrualMutation.mutateAsync({
        rentalsMerchantId: merchant.id,
        customerDocumentNumber: effectiveCustomerDocumentNumber,
        ...input,
      });
      await refetchAccrualsQuery();
    },
    [merchant?.id, effectiveCustomerDocumentNumber, retryAccrualMutation, refetchAccrualsQuery]
  );

  const { readyInvoices } = useInvoices();

  const customerSubscription: CustomerSubscription = stateQuery.data?.customerSubscription ?? null;
  const chargeableSubscription = stateQuery.data?.chargeable ?? null;
  const offer: PalommaPlusOffer = stateQuery.data?.offer ?? null;

  const hasActiveSubscription = !!customerSubscription;
  const isChargeable = !!chargeableSubscription;
  const hasPendingInvoices = hasPendingInvoicesOverride ?? readyInvoices.length > 0;
  const showSubscriptionOffer = isChargeable && !hasActiveSubscription && hasPendingInvoices;

  const selectSubscription = useCallback((value: boolean) => {
    setSelectedSubscription(value);
  }, []);

  // Determine what to charge. Backend has already resolved snapshot-or-offer.
  const subscriptionToCharge = useMemo(() => {
    if (!chargeableSubscription) return null;
    if (hasActiveSubscription || selectedSubscription) {
      return { price: chargeableSubscription.price };
    }
    return null;
  }, [chargeableSubscription, hasActiveSubscription, selectedSubscription]);

  const totalSubscriptionAmount = subscriptionToCharge?.price ?? 0;
  const subscriptionIvaBreakdown = subscriptionToCharge ? getPalommaPlusIvaBreakdown(subscriptionToCharge.price) : null;

  const cancelSubscription = useCallback(
    async (input: { reason: string; isInPencilExperiment?: boolean }) => {
      if (!merchant?.id || !effectiveCustomerDocumentNumber) return;
      await cancelMutation.mutateAsync({
        rentalsMerchantId: merchant.id,
        customerDocumentNumber: effectiveCustomerDocumentNumber,
        subscriptionChurn: input,
      });
      void stateQuery.refetch();
    },
    [merchant?.id, effectiveCustomerDocumentNumber, cancelMutation, stateQuery]
  );

  return (
    <SubscriptionsContext.Provider
      value={{
        isEnabled,
        hasActiveSubscription,
        showSubscriptionOffer,
        selectedSubscription,
        selectSubscription,
        totalSubscriptionAmount,
        subscriptionIvaBreakdown,
        subscriptionToCharge,
        customerSubscription,
        offer,
        cancelSubscription,
        experiments: experimentsQuery.data,
        isLoading: stateQuery.isPending,
        refetchActive: () => {
          void stateQuery.refetch();
        },
        puntosColombiaAccruals: accrualsQuery.data ?? [],
        puntosColombiaAccrualsLoading: accrualsQuery.isPending,
        retryPuntosColombiaAccrual,
        isRetryingPuntosColombiaAccrual: retryAccrualMutation.isPending,
        refetchPuntosColombiaAccruals,
      }}
    >
      {children}
    </SubscriptionsContext.Provider>
  );
};

export default SubscriptionsContext;
export { SubscriptionsProvider };
